<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { GameState } from './util';
import { useQuizterStore } from './stores/quizterStore';
import QuizterLayout from './layout/QuizterLayout.vue';
import SettingsScreenView from './views/host/SettingsScreenView.vue';
import ShowQuestionsView from './views/host/ShowQuestionsView.vue';
import ShowQuestionsPhraseView from './views/host/ShowQuestionPhraseView.vue';
import CorrectAnswerView from './views/host/CorrectAnswerView.vue';
import ResultsView from './views/host/ResultsView.vue';
import EndOfGameView from './views/host/EndOfGameView.vue';
import LobbyView from './views/host/LobbyView.vue';
import Card from './components/Card.vue';
import GameNotFound from './components/GameNotFound.vue';
import GameError from './components/GameError.vue';
import useLocale from '../composables/use-locale';

type Props = {
  gameId: string;
  returnParams?: {
    url: string;
    text: string;
  };
};

const props = defineProps<Props>();

const emit = defineEmits<{
  exit: [];
}>();

const { t } = useLocale();

// Store
const quizterStore = useQuizterStore();
const {
  state,
  timeLeft,
  gameOptions,
  isConnected,
  gameId: storeGameId,
  isLoading,
  isReconnecting,
  currentQuestion,
  isGameFinished,
} = storeToRefs(quizterStore);

const goToLobby = () => {
  state.value = GameState.LOBBY;
};

const goBack = () => {
  if (state.value === GameState.SETTINGS) {
    emit('exit');
    quizterStore.reset();
  } else if (state.value === GameState.LOBBY) {
    state.value = GameState.SETTINGS;
  }
};

// Watch for game ready state to request first question
watch(state, (newState, oldState) => {
  // When game becomes READY, request the first question
  if (newState === GameState.READY && oldState !== GameState.READY) {
    if (gameOptions.value?.useQuestionShowTimer) {
      quizterStore.nextQuestion();
    } else {
      quizterStore.showQuestionPhrase();
    }
  }
});

// Watch timer to automatically show correct answer when time runs out
watch(timeLeft, (newValue) => {
  // When timer reaches 0 and we're showing a question, automatically show correct answer
  if (newValue === 0 && state.value === GameState.SHOW_QUESTIONS && gameOptions.value?.useTimer) {
    quizterStore.showCorrectAnswer();
  }
});

onMounted(() => {
  // Set the gameId in the store
  quizterStore.setGameId(props.gameId);
  quizterStore.connect();

  if (props.returnParams) {
    quizterStore.setReturnParams(props.returnParams);
  }
});

onUnmounted(() => {
  quizterStore.disconnect();
});

// Join game as Host once connected
watch([isConnected], ([newConn], [oldConn]) => {
  if (newConn && !oldConn) {
    quizterStore.joinGameHost(props.gameId);
  }
});
</script>

<template>
  <QuizterLayout
    :showHeader="state !== GameState.INIT"
    :showBackButton="state === GameState.SETTINGS || state === GameState.LOBBY"
    @back="goBack"
  >
    <template #header-right v-if="storeGameId && state !== GameState.SETTINGS && state !== GameState.LOBBY">
      <p class="text-center text-h5 ma-0 mt-2 chalkboard-font">{{ storeGameId }}</p>
    </template>

    <!-- Game Not Found -->
    <GameNotFound v-if="state === GameState.GAME_NOT_FOUND" />

    <!-- Any error the host cannot act on. Without this the host is left on an empty chalkboard. -->
    <GameError v-else-if="state === GameState.UNHANDLED_ERROR" />

    <!-- Reconnecting: Show single loading card -->
    <div v-else-if="isReconnecting" class="d-flex justify-center">
      <Card max-width="500">
        <div class="d-flex flex-column align-center justify-center" style="min-height: 400px">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
          <p class="chalkboard-font text-center text-h6 ma-0 mt-8">{{ t('connecting') }}</p>
        </div>
      </Card>
    </div>

    <!-- Normal game flow -->
    <Transition v-else name="fade" mode="out-in">
      <div :key="state">
        <!-- Loading/Init: creating game -->
        <div
          v-if="state === GameState.INIT"
          class="d-flex flex-column align-center justify-center"
          style="min-height: 400px"
        >
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </div>

        <!-- Settings Screen -->
        <SettingsScreenView v-else-if="state === GameState.SETTINGS" @proceed="goToLobby" />

        <!-- Lobby Screen -->
        <LobbyView v-else-if="state === GameState.LOBBY" />

        <!-- Loading Screen (waiting for first question) -->
        <div
          v-if="state === GameState.READY && isLoading && !currentQuestion"
          class="d-flex flex-column align-center justify-center"
        >
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </div>

        <!-- Screen for just the question phrase -->
        <!-- Note: this state is only shown when playing without the question show timer, with timer only ShowQuestionsView is used -->
        <ShowQuestionsPhraseView v-else-if="state === GameState.SHOW_QUESTION_PHRASE" />

        <!-- Game Controls and Questions -->
        <ShowQuestionsView v-else-if="state === GameState.SHOW_QUESTIONS" />

        <!-- Correct Answer Screen -->
        <CorrectAnswerView v-else-if="state === GameState.SHOW_CORRECT_ANSWER" />

        <!-- Results/Leaderboard Screen -->
        <ResultsView v-else-if="state === GameState.SHOW_RESULTS && !isGameFinished" />

        <!-- End of Game Screen -->
        <EndOfGameView v-else-if="state === GameState.SHOW_RESULTS && isGameFinished" />
      </div>
    </Transition>
  </QuizterLayout>
</template>
